import {
  ChartBarIcon,
  SearchIcon,
  ShoppingCartIcon,
  TrendingUpIcon,
  GlobeIcon,
  SparklesIcon,
  ChartPieIcon,
  CloudDownloadIcon,
  ChatAlt2Icon,
  CurrencyDollarIcon,
  AdjustmentsIcon,
  TruckIcon,
  DocumentTextIcon,
  CalculatorIcon,
} from '@heroicons/react/solid'
import { HostType } from 'configs/theme'
import { c, FC } from 'lib/component-utils'
import { useUserContext } from 'lib/context'
import Container from 'components/landing/Container'
import Heading from 'components/landing/Heading'
import { useIntl } from 'lib/intl-utils'

const localizedTitle: Partial<Record<string, string>> = {
  ru: 'Чем поможет Sellmonitor?',
  uz: 'Sellmonitor sizga qanday yordam beradi?',
  en: 'How will Sellscreen help?',
  zh: 'Sellscreen 有何帮助？',
}

const localizedCases: Partial<Record<string, { name: string; cases: string[] }[]>> = {
  ru: [
    {
      name: 'Новичку',
      cases: [
        'Получить представление о состоянии рынка',
        'Найти нишу с высоким спросом и низкой конкуренцией',
        'Выбрать товары, которые будут продаваться',
      ],
    },
    {
      name: 'Действующему продавцу',
      cases: [
        'Вывести свои товары в топ выдачи с помощью SEO-оптимизации',
        'Масштабировать продажи и расширить ассортиментную матрицу',
        'Вовремя заметить трендовый товар и успеть заработать на нём',
      ],
    },
    {
      name: 'Крупному бизнесу',
      cases: [
        'Видеть свою прибыль и другие метрики на удобном дашборде',
        'Выгружать данные для использования в сторонних системах',
        'Автоматизировать ответы на отзывы для управления имиджем бренда',
      ],
    },
  ],
  uz: [
    {
      name: 'Yangi sotuvchilarga',
      cases: [
        'Marketpleys holatini tushunish',
        'Yuqori talab va past raqobatli marketpleysni topish',
        'Yaxshi sotiladigan mahsulotlarni tanlash',
      ],
    },
    {
      name: 'Amaldagi sotuvchilarga',
      cases: [
        'SEO optimizatsiyasi yordamida mahsulotlaringizni top qidiruv natijalariga chiqarish',
        'Sotuvelarni kengaytirish va assortiment matritsasini oshirish',
        "Trenddagi mahsulotlarni o'z vaqtida aniqlash va ulardan daromad olish",
      ],
    },
    {
      name: 'Yirik bizneslar uchun',
      cases: [
        "Foyda va boshqa ko'rsatkichlarni qulay dashboard orqali kuzatish",
        "Ma'lumotlarni tashqi tizimlarda foydalanish uchun yuklab olish",
        'Brend imidjini boshqarish uchun sharhlarga avtomatik javob berish',
      ],
    },
  ],
  en: [
    {
      name: 'New sellers',
      cases: [
        'Assess the state of the market',
        'Find a niche with high demand and low competition',
        'Choose products that will sell well',
      ],
    },
    {
      name: 'Existing sellers',
      cases: [
        "Use SEO to improve your products' ranking",
        'Scale your sales and expand your product range',
        'Identify trending products in time to make a profit',
      ],
    },
    {
      name: 'Large businesses',
      cases: [
        'Make intelligent decisions using accurate data',
        'Track your business metrics and compare them with competitors',
        'Export data for use in third-party systems',
      ],
    },
  ],
  zh: [
    {
      name: '新卖家',
      cases: ['评估市场状况', '寻找需求大、竞争小的细分市场', '选择畅销的产品'],
    },
    {
      name: '现有卖家',
      cases: [
        '利用搜索引擎优化提高产品排名',
        '扩大销售并拓展产品矩阵',
        '及时发现流行产品，赚取利润',
      ],
    },
    {
      name: '大型企业',
      cases: [
        '利用准确数据做出明智决策',
        '跟踪您的业务指标并与竞争对手进行比较',
        '导出数据供第三方系统使用',
      ],
    },
  ],
}

const contentByHost: Partial<
  Record<
    HostType,
    Partial<Record<string, { title: string; items: { name: string; cases: string[] }[] }>>
  >
> = {
  sellmonitor: {
    ru: {
      title: localizedTitle.ru ?? '',
      items: [
        {
          name: 'Выбрать нишу и товары',
          cases: [
            'Оценить спрос, сезонность и конкуренцию в нише',
            'Сравнить продажи и цены товаров конкурентов',
            'Найти товары для старта или расширения ассортимента',
          ],
        },
        {
          name: 'Управлять магазинами',
          cases: [
            'Отслеживать выручку, расходы и прибыль по каждому товару',
            'Рассчитывать юнит-экономику товаров до закупки',
            'Планировать поставки с учётом остатков и спроса',
          ],
        },
        {
          name: 'Сократить ручную работу',
          cases: [
            'Управлять ценами и рекламными ставками автоматически',
            'Автоматизировать ответы на отзывы по своим правилам',
            'Подбирать ключевые слова и создавать описания карточек с ИИ',
          ],
        },
      ],
    },
  },
  uzum: {
    uz: {
      title: localizedTitle.uz ?? '',
      items: localizedCases.uz ?? [],
    },
    ru: {
      title: localizedTitle.ru ?? '',
      items: localizedCases.ru ?? [],
    },
  },
  sellscreen: {
    en: {
      title: localizedTitle.en ?? '',
      items: localizedCases.en ?? [],
    },
    zh: {
      title: localizedTitle.zh ?? '',
      items: localizedCases.zh ?? [],
    },
  },
}

const casesStyles = {
  0: {
    style: 'bg-lime-100 ring-lime-300',
    icons: [ChartBarIcon, SearchIcon, ShoppingCartIcon],
    sellmonitorIcons: [ChartBarIcon, SearchIcon, ShoppingCartIcon],
    iconStyle: 'text-lime-600',
  }, // Новичку
  1: {
    style: 'bg-primary-100 ring-primary-300',
    icons: [TrendingUpIcon, GlobeIcon, SparklesIcon],
    sellmonitorIcons: [CurrencyDollarIcon, CalculatorIcon, TruckIcon],
    iconStyle: 'text-sky-700',
  }, // Действующему продавцу
  2: {
    style: 'bg-indigo-100 ring-indigo-300 col-span-1 md:col-span-2 lg:col-span-1',
    icons: [ChartPieIcon, CloudDownloadIcon, ChatAlt2Icon],
    sellmonitorIcons: [AdjustmentsIcon, ChatAlt2Icon, DocumentTextIcon],
    iconStyle: 'text-indigo-700',
  }, // Крупному бизнесу
}

const UseCasesCards: FC<{
  content?: { title: string; items: { name: string; cases: string[] }[] }
}> = ({ className, content }) => {
  const ctx = useUserContext()
  const { lang } = useIntl()
  const { title, items } = content ?? contentByHost[ctx.theme.hostType]![lang]!

  return (
    <Container className={c`${className}`}>
      <Heading tagline={title} />
      <ul className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 md:gap-8 mt-16 w-full">
        {items.map(({ name, cases }, index) => {
          const caseStyle = casesStyles[index as keyof typeof casesStyles]

          return (
            <li
              key={name}
              className={c`p-8 rounded-2xl flex flex-col text-black ring-1
                ${caseStyle.style}
              `}
            >
              <h3 className="font-medium text-2xl">{name}</h3>
              <ul className="flex flex-col gap-4 mt-6">
                {cases.map(($case, i) => {
                  const ItemIcon = (
                    ['sellmonitor', 'sellscreen'].includes(ctx.theme.hostType)
                      ? caseStyle.sellmonitorIcons
                      : caseStyle.icons
                  )[i]

                  return (
                    <li key={$case} className="flex gap-4">
                      <ItemIcon className={c`size-5 shrink-0 mt-1 ${caseStyle.iconStyle}`} />
                      <span className="text-base">{$case}</span>
                    </li>
                  )
                })}
              </ul>
            </li>
          )
        })}
      </ul>
    </Container>
  )
}

export default UseCasesCards
